/**
 * utm.ts — marketing-attribution capture (client side).
 *
 * A campaign link like https://karr.pro/?utm_source=twitter&utm_campaign=launch
 * carries UTM params that the Google OAuth redirect would otherwise drop. We
 * capture them on FIRST touch (so a later organic visit can't overwrite the
 * acquisition source) and stash them in localStorage — same origin, so they
 * survive the OAuth round-trip exactly like the ?ref= referral code. Onboarding
 * reads them and attaches them to the signup funnel event. Expire after 90 days.
 *
 * @see referral.ts — the same first-touch-survives-OAuth pattern for ?ref=.
 */

const KEY = 'karr_utm';
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

const FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

type UtmParams = Partial<Record<(typeof FIELDS)[number], string>>;

/** Read utm_* from the current URL and store them (first-touch wins). Call on app load. */
export function captureUtm(): void {
    try {
        const qs = new URLSearchParams(window.location.search);
        const captured: UtmParams = {};
        for (const f of FIELDS) {
            const v = qs.get(f);
            if (v) captured[f] = v.slice(0, 200); // guard against pathological lengths
        }
        if (Object.keys(captured).length === 0) return;

        const existing = localStorage.getItem(KEY);
        if (existing) {
            try {
                const p = JSON.parse(existing) as { params?: UtmParams; ts?: number };
                if (p?.params && Date.now() - (p.ts || 0) < MAX_AGE_MS) return; // keep first-touch
            } catch {
                /* fall through and overwrite a corrupt value */
            }
        }
        localStorage.setItem(KEY, JSON.stringify({ params: captured, ts: Date.now() }));
    } catch {
        /* SSR / storage disabled — ignore */
    }
}

/** The stored UTM params (empty object if none/expired). */
export function getStoredUtm(): UtmParams {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return {};
        const p = JSON.parse(raw) as { params?: UtmParams; ts?: number };
        if (!p?.params) return {};
        if (Date.now() - (p.ts || 0) > MAX_AGE_MS) {
            localStorage.removeItem(KEY);
            return {};
        }
        return p.params;
    } catch {
        return {};
    }
}

/** Forget the stored params (call after they've been attached to the signup event). */
export function clearStoredUtm(): void {
    try {
        localStorage.removeItem(KEY);
    } catch {
        /* ignore */
    }
}
